
import { supabase } from "@/integrations/supabase/client";
import { ASPSP } from "@/types/openpayments/bank";
import { handleError } from '@/utils/handleError';
import type { BankConstraintInfo } from "@/types/bankConstraints";
import { translateOutsideReact } from '@/i18n/translateOutsideReact';

/**
 * Client for interacting with the bank-info-api edge function
 */
export class BankInfoApiClient {
  /**
   * Get list of supported banks for a given country code
   * @param countryCode ISO 3166-1 alpha-2 country code
   * @returns Promise containing list of supported banks
   */
  static async getBanks(countryCode: string = 'SE'): Promise<{
    banks: Array<{
      bicFi: string;
      name: string;
      logoUrl: string;
      countryCode: string;
      ref: string;
      psuTypes?: string[];
    }>;
  }> {
    try {
      const { data, error } = await supabase.functions.invoke('bank-info-api', {
        body: { action: 'getBanks', countryCode }
      });
      
      if (error) throw error;
      
      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToLoadBanksFromTheServer'), 'getBanks');
      return { banks: [] };
    }
  }
  
  /**
   * Get bank details by BIC code
   * @param bic Bank Identifier Code
   * @param countryCode Optional country code to narrow search
   * @returns Promise containing bank details
   */
  static async getBankByBic(bic: string, countryCode?: string): Promise<{ bank: ASPSP | undefined }> {
    try {
      const { data, error } = await supabase.functions.invoke('bank-info-api', {
        body: { action: 'getBankByBic', bic, countryCode }
      });

      if (error) throw error;

      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToLoadBankByBic'), 'getBankByBic');
      return { bank: undefined };
    }
  }

  /**
   * Get bank constraints by BIC code
   * @param bic Bank Identifier Code
   * @returns Promise containing bank constraint or null
   */
  static async getConstraint(bic: string): Promise<BankConstraintInfo | null> {
    try {
      const { data, error } = await supabase.functions.invoke('bank-info-api', {
        body: { action: 'getConstraints', bic }
      });

      if (error) throw error;

      return (data as { constraint: BankConstraintInfo | null }).constraint ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Get list of supported countries
   * @returns Promise containing list of supported countries
   */
  static async getSupportedCountries(): Promise<{
    countries: Array<{
      code: string;
      name: string;
    }>;
  }> {
    try {
      const { data, error } = await supabase.functions.invoke('bank-info-api', {
        body: { action: 'getSupportedCountries' }
      });

      if (error) throw error;

      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToLoadSupportedCountries'), 'getSupportedCountries');
      return { countries: [] };
    }
  }
}
